import { isAction } from "@reduxjs/toolkit";
import debounce from "lodash/debounce";

import { opfsQueueStorage } from "@/Services/OPFSQueueStorage";

import {
    addOrReplaceRequestQueueItem,
    addRequestQueueItem,
    applyRequestQueueItemReplacements,
    bulkAddRequestQueueItems,
    deleteRequestQueueItem,
    patchRequestQueueItem,
    upsertSaleOrderUpdateRequestQueueItem,
} from "../slices/offline";
import { AppMiddleware, RootState } from "../types";

const DEBOUNCE_MS = 1000;

const selectQueue = (state: RootState) => state.offline.requestQueue;
const selectUser = (state: RootState) => state.user;

const debouncedSave = debounce(
    (userId: string, role: string, items: ReturnType<typeof selectQueue>) => {
        opfsQueueStorage.save(userId, role, items);
    },
    DEBOUNCE_MS,
);

const DEBOUNCED_ACTIONS = new Set<string>([patchRequestQueueItem.type]);

const INSTANT_ACTIONS = new Set<string>([
    addRequestQueueItem.type,
    addOrReplaceRequestQueueItem.type,
    upsertSaleOrderUpdateRequestQueueItem.type,
    deleteRequestQueueItem.type,
    bulkAddRequestQueueItems.type,
    applyRequestQueueItemReplacements.type,
]);

export const opfsQueueMiddleware: AppMiddleware =
    (store) => (next) => (action) => {
        const result = next(action);

        if (!isAction(action)) {
            return result;
        }

        const type = action.type;

        if (!DEBOUNCED_ACTIONS.has(type) && !INSTANT_ACTIONS.has(type)) {
            return result;
        }

        const state = store.getState();
        const user = selectUser(state);
        const queue = selectQueue(state);

        const normalizeQueue = queue.filter(
            ({ options }) => !options?.todayOnly,
        );

        if (!user?.id || !user?.role) return result;

        if (DEBOUNCED_ACTIONS.has(type)) {
            debouncedSave(user.id, user.role, normalizeQueue);
            return result;
        }

        // INSTANT_ACTIONS
        debouncedSave.cancel();
        opfsQueueStorage.save(user.id, user.role, normalizeQueue);

        return result;
    };
